<template>
    <div>
        <div v-if="itemsToDisplay.length > 0 && withHeader" class="table-col-title border-b border-base-200 mb-2">
            <SortableHeader :order="lessonPlansStore.order" property="title" @change="onChangedOrder">
                {{ $t("Title") }}
            </SortableHeader>
        </div>

        <div v-if="lessonPlansStore.loading" class="mt-10">
            <div class="flex items-center justify-center gap-2 text-base-content/60">
                <span class="loading loading-spinner loading-sm"></span>
            </div>
        </div>

        <div v-else class="flex flex-col gap-2 pb-10">
            <!-- <template v-if="infiniteScroll"> -->
                <InfiniteScroll
                    :items="itemsToDisplay"
                    :loading="isLoading"
                    :hasMore="activePage?.hasNext() || false"
                    @loadMore="handleLoadMore"
                >
                    <template #default="{ item: lessonPlan }">
                        <LessonPlanListItem
                            :lessonPlan="lessonPlan"
							:collections="filteredCollections(lessonPlan)"
							:withLibraryStatus="withLibraryStatus"
							:showCreatorName="shouldShowCreatorName(lessonPlan)"
                            @lessonPlanClicked="onLessonPlanClicked"
                        />
                    </template>
                </InfiniteScroll>
            <!-- </template>

            <template v-else>
                <LessonPlanListItem
                    v-for="(lessonPlan) in itemsToDisplay" :key="lessonPlan.id"
                    :lessonPlan="lessonPlan"
					:withLibraryStatus="withLibraryStatus"
					:showCreatorName="shouldShowCreatorName(lessonPlan)"
                    @lessonPlanClicked="onLessonPlanClicked"
                />

                <Pagination
                    v-if="activePage && activePage.lastPage > 1"
                    :page="activePage"
                    @requestPage="handleRequestPage"
                />
            </template> -->
        </div>
    </div>
</template>


<script lang="ts">

import { mapStores } from "pinia";
import { useLessonPlansStore } from '@/stores/LessonPlans.store';
import { LessonPlan } from "@/models/LessonPlan.model";
import { PaginationState } from "@/models/PaginationState.model";
import { OrderParameter } from "@/models/OrderParameter.model";
import LessonPlanListItem from '@/components/LessonPlanListItem.vue';
import SortableHeader from "@/components/ui/SortableHeader.vue";
import Pagination from "@/components/ui/Pagination.vue";
import InfiniteScroll from "@/components/ui/base/InfiniteScroll.vue";
import { useDiscoverStateStore } from '@/stores/DiscoverCollections.store';
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";


export default {
	name: "LessonPlanList",

	emits: [
		'lessonPlanClicked',
		'requestPage',
		'loadMore',
	],

	props: {
		lessonPlans: {
			type: Array as () => LessonPlan[],
			required: true,
		},
		// infiniteScroll: {
		// 	type: Boolean,
		// 	default: false,
		// },
		withHeader: {
			type: Boolean,
			default: true,
		},
		withLibraryStatus: {
			type: Boolean,
			default: false,
		},
		showCreatorName: {
			type: [Boolean, Function],
			default: false,
		},
		page: {
			type: PaginationState,
			default: null,
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},

	components: {
		LessonPlanListItem,
		SortableHeader,
		Pagination,
		InfiniteScroll,
	},

	async mounted() {
		await this.lessonPlanCollectionsStore.loadOnce();
	},

	computed: {
		...mapStores(useLessonPlansStore, useDiscoverStateStore, useLessonPlanCollectionsStore),

		itemsToDisplay() {
			// If the parent passed a specific array, use that.
			// Otherwise, fall back to the global store.
			return this.lessonPlans.length > 0
				? this.lessonPlans
				: (this.lessonPlansStore.items || []);
		},

		activePage() {
			return this.page || this.lessonPlansStore.page;
		},

		isLoading() {
			if (this.page) {
				return this.loading;
			}
			return this.lessonPlansStore.loading || this.lessonPlansStore.loadingMore;
		}
	},

	methods: {
		// filters the global collections to only those that are associated with the given lesson plan
		filteredCollections(lessonPlan: LessonPlan) {
			if (!lessonPlan.collectionIds || lessonPlan.collectionIds.length === 0) return [];
			return useLessonPlanCollectionsStore().findAll(lessonPlan.collectionIds).sort((a, b) => a.name.localeCompare(b.name));
		},

		onLessonPlanClicked(lessonPlan: LessonPlan) {
			this.$emit('lessonPlanClicked', lessonPlan);
		},

		async onChangedOrder(order: OrderParameter) {
			await this.lessonPlansStore.applyOrder(order);
		},

		// Handle page change requests from the Pagination component
		async handleRequestPage(page: any) {
			try {
				if (this.page) {
					// Parent manages pagination — emit the event
					this.$emit('requestPage', page);
				} else {
					// Drive the store directly to ensure pagination always triggers.
					await (this.lessonPlansStore as any).goToPage(page);
				}
			} catch (e) {
				console.error('Failed to go to page', e);
			}
		},

		// Handle load more for infinite scroll
		async handleLoadMore() {
			try {
				if (this.page) {
					this.$emit('loadMore');
				} else {
					await this.lessonPlansStore.loadMore();
				}
			} catch (error) {
				console.error("Failed to load more lesson plans:", error);
			}
		},

		shouldShowCreatorName(lessonPlan: LessonPlan): boolean {
			if (typeof this.showCreatorName === 'function') {
				return this.showCreatorName(lessonPlan);
			}
			return this.showCreatorName;
		},
	},
}
</script>
